<template>
    <div class="flex gap-4 flex-col">
        <div v-if="props.title" class="text-xl font-extrabold">Custom Header</div>
        <div>
            <vx-label size="lg">Name</vx-label>
            <vx-input size="lg"
                v-model="data.header_name"
                :error="displayFirstError(validate.header_name)"></vx-input>
        </div>

        <div data-test="header-value-wrapper">
            <vx-label size="lg">Value</vx-label>
            <vx-input size="lg"
                :type="showSecret ? 'text' : 'password'"
                :error="displayFirstError(validate.header_value)"
                v-model="data.header_value"
                @focus="focusSecret"
                :placeholder="data.auth_config_id ? '•••••••••' : ''"
                @blur="showSecret = false">
            </vx-input>
        </div>
        <div>
            <vx-button @click="basicModal = true"
                type="button"
                color="muted">
                Basic Auth Helper
                <font-awesome-icon widthAuto :icon="faHammer"></font-awesome-icon>
            </vx-button>

            <vx-modal v-model:visible="basicModal"
                size="md"
                padding="standard">
                <template v-slot:header>Basic Auth Helper</template>
                <template v-slot:subheader>Give us the auth and we'll make the header.</template>

                <div class="flex flex-col gap-4">
                    <div>
                        <vx-label size="lg">Username</vx-label>
                        <vx-input autocomplete="off"
                            size="lg"
                            v-model="basic.username"
                            :error="displayFirstError(basicValidation.username)"
                            @keypress.enter="makeHeaders()"></vx-input>
                    </div>

                    <div>
                        <vx-label size="lg">Password</vx-label>
                        <vx-input size="lg"
                            autocomplete="off"
                            :type="showSecret ? 'text' : 'password'"
                            :error="displayFirstError(basicValidation.password)"
                            v-model="basic.password"
                            @keypress.enter="makeHeaders()">
                            <template v-slot:right>
                                <vx-button class="-mr-1"
                                    @click.prevent="showSecret = !showSecret"
                                    color="transparent"
                                    size="sm">
                                    <font-awesome-icon widthAuto :icon="showSecret ? faEyeSlash : faEye"></font-awesome-icon>
                                </vx-button>
                            </template>
                        </vx-input>
                    </div>
                </div>

                <template v-slot:footer>
                    <div class="flex flex-col-reverse gap-4 lg:flex-row">
                        <vx-button @click="basicModal = false"
                            type="button"
                            class="grow lg:w-20 lg:grow-0"
                            size="lg"
                            color="muted">
                            Cancel
                        </vx-button>
                        <vx-button type="button"
                            data-test="make-header"
                            @click="makeHeaders()"
                            class="grow"
                            size="lg"
                            color="primary">
                            Make Headers <font-awesome-icon widthAuto :icon="faHammer"></font-awesome-icon>
                        </vx-button>
                    </div>
                </template>
            </vx-modal>
        </div>
    </div>


</template>

<script setup>
import { faEye, faEyeSlash, faHammer } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
    VxButton,
    VxInput,
    VxLabel,
    VxModal,
} from '@voxie/frontend-components';
import useVuelidate from "@vuelidate/core";
import { required, requiredIf } from "@vuelidate/validators";
import { computed, nextTick, reactive, ref } from 'vue';
import { useAuthConfigForm } from "~/components/settings/integrations/auth-configs/form/useAuthConfigForm";
import { displayFirstError } from "~/utils/validation";

const props = defineProps({
    title: {
        type: Boolean,
        default: false,
    }
});

const { data } = useAuthConfigForm();

const showSecret = ref(false);

const basicModal = ref(false);
const basic = reactive({
    username: '',
    password: '',
});

const basicValidation = useVuelidate(computed(() => ({
    username: {
        requiredIf: requiredIf(() => basicModal.value),
    },
    password: {
        requiredIf: requiredIf(() => basicModal.value),
    },
})), basic)

const makeHeaders = () => {
    data.value.header_name = 'Authorization';
    data.value.header_value = 'Basic ' + btoa(`${basic.username}:${basic.password}`);

    showSecret.value = false;
    basic.username = '';
    basic.password = '';

    basicModal.value = false;
}

const focusSecret = async () => {
    await nextTick();
    showSecret.value = true;
}

const rules = {
    header_name: {
        required,
    },
    header_value: {
        required,
    },
};

const validate = useVuelidate(rules, data);

defineExpose({
    basicModal: basicModal
})
</script>
